import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { font } from '../../utils/fontHelper';
import ImageShimmer from '../widgets/ImageShimmer';

const MOVIE = {
  title: 'Cinema Paradiso',
  year: '1988',
  genre: 'Drama/Romance',
  runtime: '124 min',
  director: 'Giuseppe Tornatore',
  imdb: '8.4',
  watchlistRating: '4.3',
  plot: 'A filmmaker recalls his childhood, when he fell in love with the movies at his village\'s cinema and formed a deep friendship with the theater\'s projectionist.',
  poster: 'https://image.tmdb.org/t/p/w500/gEBMJgFMoKFDsK0O3rRFRkCmYNk.jpg',
};

// ── Mock frame ────────────────────────────────────────────────────────────────

const MockFrame: React.FC<{ label: string; description: string; children: React.ReactNode }> = ({
  label,
  description,
  children,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 5 }}>
      <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 15, color: theme.palette.text.primary, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, mb: 1.5 }}>
        {description}
      </Typography>
      <Box sx={{ maxWidth: 375 }}>
        {children}
      </Box>
    </Box>
  );
};

// ── Shared sub-components ─────────────────────────────────────────────────────

const TmdbBadge: React.FC<{ rating: string; color?: string }> = ({ rating, color = 'rgba(255,255,255,0.75)' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <img src="/assets/Icons/tmdb_logo.svg" alt="TMDb" style={{ height: 8, width: 'auto' }} />
    <Typography sx={{ fontFamily: font(), fontSize: 12, color }}>{rating}</Typography>
  </Box>
);

const WatchlistBadge: React.FC<{ rating: string; color?: string }> = ({ rating, color = 'rgba(255,255,255,0.75)' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography sx={{ fontSize: 14, lineHeight: 1 }}>✦✦</Typography>
    <Typography sx={{ fontFamily: font(), fontSize: 12, color }}>{rating}</Typography>
  </Box>
);

// ── Variant A — Current Layout ────────────────────────────────────────────────

const VariantA: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      background: isDark ? 'rgba(44,44,46,0.4)' : 'rgba(245,245,245,0.8)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: 6,
      p: '24px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      {/* Row */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Poster */}
        <Box sx={{ width: 113, height: 169, borderRadius: 1.25, overflow: 'hidden', flexShrink: 0 }}>
          <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={1.25} />
        </Box>
        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Group 1: title + meta + director */}
          <Box>
            <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 20, lineHeight: 1.25, color: theme.palette.text.primary, mb: 0.5 }}>
              {MOVIE.title}
            </Typography>
            <Typography sx={{ fontFamily: font(), fontSize: 14, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 0.5 }}>
              {MOVIE.year} • {MOVIE.genre} • {MOVIE.runtime}
            </Typography>
            <Typography sx={{ fontFamily: font(), fontSize: 13, color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)' }}>
              Directed by <span style={{ fontWeight: 500, color: isDark ? '#7D7AFF' : '#3634A3', textDecoration: 'underline', textUnderlineOffset: 2 }}>{MOVIE.director}</span>
            </Typography>
          </Box>
          {/* Group 2: ratings + description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TmdbBadge rating={MOVIE.imdb} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
              <WatchlistBadge rating={MOVIE.watchlistRating} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
            </Box>
            <Typography sx={{ fontFamily: font(), fontSize: 12, lineHeight: 1.5, color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {MOVIE.plot}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant B — Full-Bleed Poster with Overlay ────────────────────────────────

const VariantB: React.FC = () => {
  const theme = useTheme();
  return (
    <Box sx={{ borderRadius: 6, overflow: 'hidden', border: `1px solid rgba(255,255,255,0.08)` }}>
      {/* Poster with gradient scrim */}
      <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
        <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={0} />
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)',
        }} />
        {/* Overlay content */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: '16px 20px' }}>
          <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 22, color: '#fff', lineHeight: 1.2, mb: 0.25 }}>
            {MOVIE.title}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 13, color: 'rgba(255,255,255,0.7)', mb: 0.75 }}>
            {MOVIE.year} • {MOVIE.genre} • {MOVIE.runtime}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TmdbBadge rating={MOVIE.imdb} />
            <WatchlistBadge rating={MOVIE.watchlistRating} />
          </Box>
        </Box>
      </Box>
      {/* Description below */}
      <Box sx={{ bgcolor: 'rgba(0,0,0,0.85)', p: '12px 20px' }}>
        <Typography sx={{ fontFamily: font(), fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: 0.25 }}>
          Directed by <span style={{ color: '#7D7AFF' }}>{MOVIE.director}</span>
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 12, lineHeight: 1.5, color: 'rgba(255,255,255,0.8)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {MOVIE.plot}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Variant C — Glassmorphism ─────────────────────────────────────────────────

const VariantC: React.FC = () => {
  return (
    <Box sx={{ position: 'relative', borderRadius: 6, overflow: 'hidden', minHeight: 200 }}>
      {/* Background: blurred poster */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={0} />
      </Box>
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)' }} />
      {/* Glass content panel */}
      <Box sx={{
        position: 'relative',
        display: 'flex',
        gap: 2,
        p: '20px 20px',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}>
        {/* Poster thumbnail */}
        <Box sx={{ width: 100, height: 150, borderRadius: 2, overflow: 'hidden', flexShrink: 0, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={2} />
        </Box>
        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 19, color: '#fff', lineHeight: 1.25 }}>
            {MOVIE.title}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
            {MOVIE.year} • {MOVIE.genre}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
            Directed by <span style={{ color: '#A09CFF' }}>{MOVIE.director}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
            <TmdbBadge rating={MOVIE.imdb} />
            <WatchlistBadge rating={MOVIE.watchlistRating} />
          </Box>
          <Typography sx={{ fontFamily: font(), fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.75)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25 }}>
            {MOVIE.plot}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant D — Compact Horizontal, Full-Width Description ────────────────────

const VariantD: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      background: isDark ? 'rgba(44,44,46,0.4)' : 'rgba(245,245,245,0.8)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: 6,
      p: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}>
      {/* Compact row */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ width: 80, height: 120, borderRadius: 1.25, overflow: 'hidden', flexShrink: 0 }}>
          <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={1.25} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 18, lineHeight: 1.25, color: theme.palette.text.primary, mb: 0.5 }}>
            {MOVIE.title}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 13, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 0.25 }}>
            {MOVIE.year} • {MOVIE.genre} • {MOVIE.runtime}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 12, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', mb: 0.75 }}>
            Directed by <span style={{ color: isDark ? '#7D7AFF' : '#3634A3' }}>{MOVIE.director}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TmdbBadge rating={MOVIE.imdb} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
            <WatchlistBadge rating={MOVIE.watchlistRating} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
          </Box>
        </Box>
      </Box>
      {/* Full-width description below */}
      <Box sx={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, pt: 1.25 }}>
        <Typography sx={{ fontFamily: font(), fontSize: 12, lineHeight: 1.6, color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.75)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {MOVIE.plot}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Variant E — Color-Tinted Gradient Card ────────────────────────────────────

const VariantE: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Simulated dominant color from Cinema Paradiso poster (warm amber/gold)
  const accentColor = isDark ? 'rgba(180, 110, 40, 0.35)' : 'rgba(180, 110, 40, 0.15)';
  const gradientBg = isDark
    ? `linear-gradient(135deg, rgba(180,110,40,0.3) 0%, rgba(30,20,10,0.6) 100%)`
    : `linear-gradient(135deg, rgba(255,200,120,0.3) 0%, rgba(245,240,230,0.9) 100%)`;

  return (
    <Box sx={{
      background: gradientBg,
      border: `1px solid ${accentColor}`,
      borderRadius: 6,
      p: '24px 20px',
      display: 'flex',
      gap: 2,
      alignItems: 'flex-start',
    }}>
      <Box sx={{ width: 113, height: 169, borderRadius: 1.25, overflow: 'hidden', flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
        <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={1.25} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 20, lineHeight: 1.25, color: theme.palette.text.primary, mb: 0.5 }}>
            {MOVIE.title}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 14, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 0.5 }}>
            {MOVIE.year} • {MOVIE.genre} • {MOVIE.runtime}
          </Typography>
          <Typography sx={{ fontFamily: font(), fontSize: 13, color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)' }}>
            Directed by <span style={{ color: isDark ? '#C89840' : '#8B5E10', fontWeight: 500 }}>{MOVIE.director}</span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TmdbBadge rating={MOVIE.imdb} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
            <WatchlistBadge rating={MOVIE.watchlistRating} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
          </Box>
          <Typography sx={{ fontFamily: font(), fontSize: 12, lineHeight: 1.5, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {MOVIE.plot}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Variant F — Stacked (poster top, info below) ──────────────────────────────

const VariantF: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{
      background: isDark ? 'rgba(44,44,46,0.4)' : 'rgba(245,245,245,0.8)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      {/* Full-width poster */}
      <Box sx={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden' }}>
        <ImageShimmer src={MOVIE.poster} alt={MOVIE.title} width="100%" height="100%" objectFit="cover" borderRadius={0} />
      </Box>
      {/* Info below */}
      <Box sx={{ p: '16px 20px', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 22, lineHeight: 1.2, color: theme.palette.text.primary }}>
          {MOVIE.title}
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 14, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
          {MOVIE.year} • {MOVIE.genre} • {MOVIE.runtime}
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 13, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
          Directed by <span style={{ color: isDark ? '#7D7AFF' : '#3634A3', fontWeight: 500 }}>{MOVIE.director}</span>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
          <TmdbBadge rating={MOVIE.imdb} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
          <WatchlistBadge rating={MOVIE.watchlistRating} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'} />
        </Box>
        <Typography sx={{ fontFamily: font(), fontSize: 13, lineHeight: 1.6, color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.75)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.25 }}>
          {MOVIE.plot}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const VARIANTS = [
  {
    label: 'A — Current Layout',
    description: 'Poster left (113×169), info column right with two groups: title/meta/director and ratings/description.',
    Component: VariantA,
  },
  {
    label: 'B — Full-Bleed Poster with Overlay',
    description: 'Poster fills the top of the card with a gradient scrim. Title, meta and ratings are pinned over the image. Description below.',
    Component: VariantB,
  },
  {
    label: 'C — Glassmorphism',
    description: 'Blurred poster as card background. Content sits in a frosted glass layer over the top — cinematic and immersive.',
    Component: VariantC,
  },
  {
    label: 'D — Compact with Full-Width Description',
    description: 'Smaller poster (80×120) keeps the row short. Description breaks out below as a full-width strip for better readability.',
    Component: VariantD,
  },
  {
    label: 'E — Color-Tinted Gradient',
    description: "Card background gradient is tinted with the poster's dominant colour — makes each movie feel distinct without extra UI.",
    Component: VariantE,
  },
  {
    label: 'F — Stacked (poster top)',
    description: 'Poster spans full width in a 16:9 ratio at the top, all info stacked below. Maximum hierarchy and readability.',
    Component: VariantF,
  },
];

const MovieHeroPreviewPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, py: 3, minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 20, color: theme.palette.text.primary, mb: 0.5 }}>
        Movie Hero Card Ideas
      </Typography>
      <Typography sx={{ fontFamily: font(), fontSize: 13, color: theme.palette.text.secondary, mb: 3 }}>
        6 layout directions for the hero card at the top of Movie Detail.
      </Typography>

      {VARIANTS.map(({ label, description, Component }) => (
        <MockFrame key={label} label={label} description={description}>
          <Component />
        </MockFrame>
      ))}
    </Box>
  );
};

export default MovieHeroPreviewPage;
